import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { getApiUrl } from "../../../../../../config/api";

// ── Add-Category Modal ────────────────────────────────────────────────────────
function AddCategoryModal({ language, open, onClose, onCreated }) {
    const [categoryName, setCategoryName] = useState("");
    const [subCategoryName, setSubCategoryName] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setCategoryName("");
            setSubCategoryName("");
            setError("");
            setSaving(false);
        }
    }, [open]);

    const handleCreate = async () => {
        if (!categoryName.trim()) return;
        setSaving(true);
        setError("");
        try {
            const token = await (
                await import("../../../../../../firebase/firebase")
            ).auth.currentUser?.getIdToken();
            await axios.post(
                `${getApiUrl()}/api/v2/competitor-categories/`,
                {
                    category_name: categoryName.trim(),
                    sub_category: subCategoryName.trim() || null,
                },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            onCreated();
            onClose();
        } catch (err) {
            setError(
                err.response?.data?.error ||
                    (language === "zh"
                        ? "建立失敗，請再試一次。"
                        : "Creation failed. Please try again."),
            );
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl animate-slideUp">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
                <div className="p-6">
                    <div className="w-12 h-12 bg-orange-500/10 rounded-2xl flex items-center justify-center mb-4 text-orange-500">
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M12 4v16m8-8H4"
                            />
                        </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {language === "zh"
                            ? "新增競品類別"
                            : "Add New Category"}
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">
                        {language === "zh"
                            ? "輸入類別名稱，子類別為選填。"
                            : "Enter a category name. Sub-category is optional."}
                    </p>
                    <div className="space-y-4">
                        {/* Category name */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                {language === "zh"
                                    ? "類別名稱"
                                    : "Category Name"}
                                <span className="text-red-400 ml-0.5">*</span>
                            </label>
                            <input
                                type="text"
                                value={categoryName}
                                onChange={(e) =>
                                    setCategoryName(e.target.value)
                                }
                                placeholder={
                                    language === "zh"
                                        ? "例：Thermal Interface Material"
                                        : "e.g. Thermal Interface Material"
                                }
                                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
                            />
                        </div>
                        {/* Sub-category name (optional) */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                {language === "zh"
                                    ? "子類別名稱"
                                    : "Sub-category Name"}
                                <span className="text-gray-300 text-xs ml-1">
                                    ({language === "zh" ? "選填" : "optional"})
                                </span>
                            </label>
                            <input
                                type="text"
                                value={subCategoryName}
                                onChange={(e) =>
                                    setSubCategoryName(e.target.value)
                                }
                                placeholder={
                                    language === "zh"
                                        ? "例：High Performance Thermal Pad"
                                        : "e.g. High Performance Thermal Pad"
                                }
                                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
                            />
                        </div>
                    </div>
                    {error && (
                        <div className="mt-4 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                            {error}
                        </div>
                    )}
                    <div className="flex items-center justify-end gap-3 mt-6">
                        <button
                            onClick={onClose}
                            disabled={saving}
                            className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                        >
                            {language === "zh" ? "取消" : "Cancel"}
                        </button>
                        <button
                            onClick={handleCreate}
                            disabled={saving || !categoryName.trim()}
                            className={`px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 ${
                                saving || !categoryName.trim()
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-500/20"
                            }`}
                        >
                            {saving ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    {language === "zh"
                                        ? "建立中…"
                                        : "Creating…"}
                                </>
                            ) : (
                                <>
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 4v16m8-8H4"
                                        />
                                    </svg>
                                    {language === "zh" ? "建立" : "Create"}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
/**
 * Group flat category rows by category_name.
 * Returns an array of:
 *   { category_name, sub_categories: [ { category_id, sub_category } | null ] }
 * A group has sub_categories only when every row has a non-null sub_category.
 * The "no sub-category" entry is represented as null in the array.
 */
function groupCategories(rows) {
    const map = {};
    for (const row of rows) {
        if (!map[row.category_name]) map[row.category_name] = [];
        map[row.category_name].push(row);
    }
    return Object.entries(map).map(([name, entries]) => ({
        category_name: name,
        // If any entry has no sub_category, the whole group has no subs
        has_sub_categories: entries.every((e) => e.sub_category),
        rows: entries,
    }));
}

// ── Main Component ────────────────────────────────────────────────────────────
function CategoriesSelection({ language, currentUser, onBack, onNext }) {
    const [rawCategories, setRawCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState("");

    // Step 1 — grouped category
    const [selectedGroup, setSelectedGroup] = useState(null);
    // Step 2 — specific row (with or without sub_category)
    const [selectedRow, setSelectedRow] = useState(null);

    const [showAddCategory, setShowAddCategory] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setFetchError("");
        try {
            const token = await currentUser.getIdToken();
            const res = await axios.get(
                `${getApiUrl()}/api/v2/competitor-categories/`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );
            setRawCategories(res.data?.data || []);
        } catch {
            setFetchError(
                language === "zh"
                    ? "無法載入資料，請稍後再試。"
                    : "Failed to load data. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    }, [currentUser, language]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const groups = groupCategories(rawCategories);

    const handleSelectGroup = (group) => {
        setSelectedGroup(group);
        setSelectedRow(null);
        // If the group has no sub-categories, auto-select the single row
        if (!group.has_sub_categories) {
            setSelectedRow(group.rows[0]);
        }
    };

    // canProceed: group chosen AND (no subs → auto-selected, or sub explicitly picked)
    const canProceed = selectedRow !== null;

    const handleNext = () => {
        if (!canProceed) return;
        onNext({
            categoryId: selectedRow.category_id,
            categoryName: selectedRow.category_name,
            subCategoryId: selectedRow.sub_category
                ? selectedRow.category_id
                : null,
            subCategoryName: selectedRow.sub_category ?? null,
        });
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-500 text-sm">
                    {language === "zh" ? "載入中…" : "Loading…"}
                </p>
            </div>
        );
    }

    if (fetchError) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
                <p className="text-red-500 text-sm">{fetchError}</p>
                <button
                    onClick={fetchData}
                    className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                    {language === "zh" ? "重試" : "Retry"}
                </button>
            </div>
        );
    }

    return (
        <div className="animate-slideUp flex justify-center">
            <div className="w-full max-w-4xl">
                {/* Header row */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onBack}
                            className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                {language === "zh"
                                    ? "選擇廠商與類別"
                                    : "Select Competitor & Category"}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-300 mt-0.5">
                                {language === "zh"
                                    ? "請先選擇競品廠商與類別，再上傳 PDF"
                                    : "Choose a competitor and category before uploading the PDF"}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Step 2 — Category */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            {language === "zh"
                                ? "步驟 2 — 選擇類別"
                                : "Step 2 — Select Category"}
                        </p>
                        <button
                            onClick={() => setShowAddCategory(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-orange-600 bg-orange-500/10 hover:bg-orange-500/20 border-2 border-orange-500/20 hover:border-orange-500/40 rounded-xl transition-all"
                        >
                            <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                />
                            </svg>
                            {language === "zh" ? "新增類別" : "New Category"}
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {groups.map((group) => {
                            const isSelected =
                                selectedGroup?.category_name ===
                                group.category_name;
                            return (
                                <button
                                    key={group.category_name}
                                    onClick={() => handleSelectGroup(group)}
                                    className={`group bg-white border-2 rounded-2xl p-6 text-left transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 hover:ring-4 focus:outline-none ${
                                        isSelected
                                            ? "border-orange-500/50 ring-4 ring-orange-500/20 shadow-lg shadow-orange-500/10"
                                            : "border-gray-200 hover:border-orange-500/50 hover:ring-orange-500/20"
                                    }`}
                                >
                                    <div
                                        className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 ${isSelected ? "bg-orange-500 text-white" : "bg-orange-500/10 text-orange-500"}`}
                                    >
                                        <svg
                                            className="w-6 h-6"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={1.5}
                                                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                                            />
                                        </svg>
                                    </div>
                                    <h3
                                        className={`text-sm font-bold mb-1 ${isSelected ? "text-orange-600" : "text-gray-900"}`}
                                    >
                                        {group.category_name}
                                    </h3>
                                    {group.has_sub_categories && (
                                        <p className="text-xs text-gray-400">
                                            {group.rows.length}{" "}
                                            {language === "zh"
                                                ? "個子類別"
                                                : "sub-categories"}
                                        </p>
                                    )}
                                    {isSelected && (
                                        <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-orange-500">
                                            <svg
                                                className="w-4 h-4"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            {language === "zh"
                                                ? "已選擇"
                                                : "Selected"}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Step 3 — Sub-category (only if the selected group has sub-categories) */}
                {selectedGroup?.has_sub_categories && (
                    <div className="mb-8">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                            {language === "zh"
                                ? "步驟 3 — 選擇子類別"
                                : "Step 3 — Select Sub-category"}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {selectedGroup.rows.map((row) => {
                                const isSelected =
                                    selectedRow?.category_id ===
                                    row.category_id;
                                return (
                                    <button
                                        key={row.category_id}
                                        onClick={() => setSelectedRow(row)}
                                        className={`group bg-white border-2 rounded-2xl p-5 text-left transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 hover:ring-4 focus:outline-none ${
                                            isSelected
                                                ? "border-orange-500/50 ring-4 ring-orange-500/20 shadow-lg shadow-orange-500/10"
                                                : "border-gray-200 hover:border-orange-500/50 hover:ring-orange-500/20"
                                        }`}
                                    >
                                        <div
                                            className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 ${isSelected ? "bg-orange-500 text-white" : "bg-orange-500/10 text-orange-500"}`}
                                        >
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={1.5}
                                                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                                />
                                            </svg>
                                        </div>
                                        <h3
                                            className={`text-sm font-bold ${isSelected ? "text-orange-600" : "text-gray-900"}`}
                                        >
                                            {row.sub_category}
                                        </h3>
                                        {isSelected && (
                                            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-orange-500">
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                                {language === "zh"
                                                    ? "已選擇"
                                                    : "Selected"}
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Summary pill */}
                {canProceed && (
                    <div className="mb-6 px-5 py-3 bg-orange-50 border border-orange-200 rounded-2xl flex items-center gap-2.5 text-sm text-orange-700">
                        <svg
                            className="w-5 h-5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                        <span>
                            <span className="font-semibold">
                                {selectedRow.category_name}
                            </span>
                            {selectedRow.sub_category && (
                                <>
                                    {" › "}
                                    <span className="font-semibold">
                                        {selectedRow.sub_category}
                                    </span>
                                </>
                            )}
                        </span>
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center justify-between pt-2">
                    <button
                        onClick={onBack}
                        className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                    >
                        {language === "zh" ? "返回" : "Back"}
                    </button>
                    <button
                        onClick={handleNext}
                        disabled={!canProceed}
                        className={`px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 ${
                            canProceed
                                ? "bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30"
                                : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                    >
                        {language === "zh"
                            ? "下一步：上傳 PDF"
                            : "Next: Upload PDF"}
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            <AddCategoryModal
                language={language}
                open={showAddCategory}
                onClose={() => setShowAddCategory(false)}
                onCreated={() => {
                    setSelectedGroup(null);
                    setSelectedRow(null);
                    fetchData();
                }}
            />
        </div>
    );
}

export default CategoriesSelection;
